/**
 * Public Roadmap Page Component
 * Minimal roadmap page - redirects to Discord for detailed discussions
 */

import React from 'react';
import { Link } from 'react-router-dom';
import IconDaggerheart from '../common/IconDaggerheart';

export default function Roadmap() {
  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 hover:text-red-400 transition-colors">
            <IconDaggerheart className="w-8 h-8 text-red-500" />
            <span className="text-xl font-bold">Chaggerheart</span>
          </Link>
          <Link to="/" className="text-gray-400 hover:text-gray-100">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8">Product Roadmap</h1>

        {/* Current Status */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-green-400 mb-4">Now: Open Beta</h2>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <p className="text-gray-300 mb-4">
              Open beta is live. Sign in with Google to get started, no waitlist. Core features:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Full character creation and management
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                All 189 domain cards with click-to-cast
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Campaign tools and GM dashboard
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Wild Recipe generation (Beast Feast)
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Character portrait studio
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Session notes with rich editing
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                Homebrew Workshop with visual mechanic composer and balance analysis
              </li>
            </ul>
          </div>
        </section>

        {/* Coming Soon */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-blue-400 mb-4">Coming Soon: The Campaign Chronicler</h2>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <p className="text-gray-300 mb-4">
              Our flagship feature: automatic session documentation.
            </p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-center gap-2">
                <span className="text-blue-400">→</span>
                Discord voice transcription with speaker ID
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-400">→</span>
                Automatic moment extraction
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-400">→</span>
                GM and player session summaries
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-400">→</span>
                Campaign-long narrative memory
              </li>
            </ul>
          </div>
        </section>

        {/* On the Horizon */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-purple-400 mb-4">On the Horizon</h2>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-center gap-2">
                <span className="text-purple-400">◦</span>
                Character building assistant with party-aware recommendations
              </li>
              <li className="flex items-center gap-2">
                <span className="text-purple-400">◦</span>
                Scene immersion illustrations
              </li>
              <li className="flex items-center gap-2">
                <span className="text-purple-400">◦</span>
                NPC relationship tracking
              </li>
              <li className="flex items-center gap-2">
                <span className="text-purple-400">◦</span>
                VTT integrations (Owlbear Rodeo, Foundry)
              </li>
            </ul>
          </div>
        </section>

        {/* Join the Conversation */}
        <section className="text-center py-8">
          <h2 className="text-2xl font-bold mb-4">Shape the Roadmap</h2>
          <p className="text-gray-400 mb-6">
            Beta testers have direct input on what we build next.
            Join our Discord to be part of the conversation.
          </p>
          <a
            href="https://discord.gg/uYf6WCQ4z7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-red-700 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Join Discord
          </a>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800 mt-16">
        <div className="max-w-4xl mx-auto text-center text-sm text-gray-500">
          <p className="mb-4">Compatible with Daggerheart under the DPCGL.</p>
          <div className="flex flex-wrap justify-center gap-6 mb-4">
            <Link to="/about" className="hover:text-gray-300">About</Link>
            <Link to="/privacy" className="hover:text-gray-300">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-300">Terms of Service</Link>
          </div>
          <p>© 2025-2026 Chag Innovations LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
